import React, { useState, useEffect } from 'react';
import { registerSoloTournament, fetchTournamentDetails } from '../../api/TournamentsApi';

const SoloTournamentRegistrationModal = ({ isOpen, onClose, tournamentId, game, isRegistered }) => {
	const [tournamentDetails, setTournamentDetails] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [success, setSuccess] = useState(false);
	const [formData, setFormData] = useState({
		username: '',
		teamName: '',
		gameId: '',
	});

	useEffect(() => {
		const fetchDetails = async () => {
			if (!tournamentId) return;

			try {
				setLoading(true);
				const details = await fetchTournamentDetails(tournamentId);
				setTournamentDetails(details);
				setFormData({
					...formData,
					gameId: details.game_id || '',
				});
			} catch (err) {
				setError('Failed to load tournament details. Please try again later.');
			} finally {
				setLoading(false);
			}
		};

		fetchDetails();
	}, [tournamentId]);

	const handleChange = (e) => {
		const { name, value } = e.target;
		setFormData({
			...formData,
			[name]: value,
		});
	};

	const handleSubmit = async (e) => {
		e.preventDefault();

		try {
			await registerSoloTournament(tournamentId, formData);
			setSuccess(true);
			setError(null);
			// Optionally, you can fetch the updated tournament details here
		} catch (err) {
			setError('Registration failed. Please check your details and try again.');
			setSuccess(false);
		}
	};

	if (loading) {
		return (
			<div className="flex justify-center items-center h-64">
				<div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
			</div>
		);
	}

	if (error) {
		return (
			<div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
				<p className="font-bold">Error</p>
				<p>{error}</p>
			</div>
		);
	}

	return (
		<div className={`fixed inset-0 z-50 overflow-auto bg-black bg-opacity-80 backdrop-blur-sm flex items-center justify-center p-4 ${isOpen ? '' : 'hidden'}`}>
			<div className="bg-gradient-to-b from-gray-900 to-gray-800 text-white rounded-lg shadow-2xl max-w-3xl w-full p-6">
				<h2 className="text-xl font-bold mb-4">
					{isRegistered ? 'Registration Details' : 'Register for Solo Tournament'}
				</h2>

				{success ? (
					<div className="bg-green-900/40 border border-green-600 text-green-200 px-4 py-3 rounded-lg mb-4">
						<p className="font-bold">Success</p>
						<p>You have successfully registered for the tournament!</p>
					</div>
				) : null}

				<form onSubmit={handleSubmit} className="space-y-4">
					<div>
						<label className="block text-sm font-medium text-gray-300 mb-1">
							Username
						</label>
						<input
							type="text"
							name="username"
						 value={formData.username}
							onChange={handleChange}
							className="w-full p-3 bg-gray-800 border border-gray-700 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
							required
						/>
					</div>

					<div>
						<label className="block text-sm font-medium text-gray-300 mb-1">
							Team Name (optional)
						</label>
						<input
							type="text"
							name="teamName"
							value={formData.teamName}
							onChange={handleChange}
							className="w-full p-3 bg-gray-800 border border-gray-700 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
						/>
					</div>

					<div>
						<label className="block text-sm font-medium text-gray-300 mb-1">
							Game ID
						</label>
						<input
							type="text"
							name="gameId"
							value={formData.gameId}
							onChange={handleChange}
							className="w-full p-3 bg-gray-800 border border-gray-700 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
							required
						/>
					</div>

					<div className="flex justify-end gap-2">
						<button
							type="button"
							onClick={onClose}
							className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-md transition-colors"
						>
							Cancel
						</button>
						<button
							type="submit"
							className="px-4 py-2 bg-blue-600 hover:bg-blue-500 rounded-md transition-colors"
						>
							{isRegistered ? 'Update Registration' : 'Register'}
						</button>
					</div>
				</form>

				{/* Registration details section - visible only if registered */}
				{isRegistered && tournamentDetails && (
					<div className="mt-6 pt-4 border-t border-gray-700">
						<h3 className="text-lg font-semibold mb-2">Your Registration Details</h3>
						
						<div className="grid grid-cols-2 gap-4 text-sm text-gray-300">
							<div>
								<span className="font-medium">Tournament:</span> {tournamentDetails.name}
							</div>
							<div>
								<span className="font-medium">Status:</span> {tournamentDetails.tournament_status.replace('_', ' ')}
							</div>
							<div>
								<span className="font-medium">Game ID:</span> {tournamentDetails.game_id}
							</div>
							<div>
								<span className="font-medium">Username:</span> {tournamentDetails.registered_players?.find(player => player.is_current_user)?.game_username}
							</div>
							<div>
								<span className="font-medium">Team Name:</span> {tournamentDetails.registered_players?.find(player => player.is_current_user)?.team_name || 'N/A'}
							</div>
							<div>
								<span className="font-medium">Registration Date:</span> {new Date(tournamentDetails.registration_date).toLocaleString()}
							</div>
						</div>
					</div>
				)}
			</div>
		</div>
	);
};

export default SoloTournamentRegistrationModal;